import { Routes } from '@angular/router';

import { AddClientComponent } from '@components/clients/add-client/add-client.component';
import { ClientsComponent } from '@components/clients/clients.component';
import { ManageOrdersComponent } from '@components/orders/manage-orders/manage-orders.component';
import { ViewOrdersComponent } from '@components/orders/manage-orders/view-orders/view-orders/view-orders.component';
import { OrderListViewComponent } from '@components/orders/saved-lists/order-list-view/order-list-view.component';
import { ViewQuotesComponent } from '@components/quotes/view-quotes/view-quotes.component';
import { ActiveUsersComponent } from '@components/users/active-users/active-users.component';
import { AddUserComponent } from '@components/users/add-user/add-user.component';
import { ArchivedUsersComponent } from '@components/users/archived-users/archived-users.component';
import { UserDetailsComponent } from '@components/users/user-details/user-details.component';
import { UsersComponent } from '@components/users/users.component';
import { AuthGuard } from '@core/guards/auth.guard';
import { HomeRedirectGuard } from '@core/guards/home-redirect.guard';
import { LoginTokenGuard } from '@core/guards/login-token.guard';

import { EditClientComponent } from '../@components/clients/edit-client/edit-client.component';
import { HomeComponent } from '../@components/home/home.component';
import { MyOrdersComponent } from '../@components/home/my-orders/my-orders.component';
import { MyQuotesComponent } from '../@components/home/my-quotes/my-quotes.component';
import { ActiveOrdersComponent } from '../@components/orders/active-orders/active-orders.component';
import { OrdersComponent } from '../@components/orders/orders.component';
import { SavedListsComponent } from '../@components/orders/saved-lists/saved-lists.component';
import { ActiveQuotesComponent } from '../@components/quotes/active-quotes/active-quotes.component';
import { ArchivedQuotesComponent } from '../@components/quotes/archived-quotes/archived-quotes.component';
import { ManageQuoteComponent } from '../@components/quotes/manage-quote/manage-quote.component';
import { QuotesComponent } from '../@components/quotes/quotes.component';
import { EditUserComponent } from '../@components/users/edit-user/edit-user.component';
import { PendingChangesGuard } from '../@core/guards/pending-changes.guard';
import { AuthLayoutComponent } from '../@layout/auth-layout/auth-layout.component';
import { MainLayoutComponent } from '../@layout/main-layout/main-layout.component';

export const routes: Routes = [
  {
    path: 'auth',
    component: AuthLayoutComponent,
    children: [
      {
        path: 'login',
        canActivate: [LoginTokenGuard],
        loadComponent: () =>
          import('@components/login/login.component').then((c) => c.LoginComponent),
      },
      { path: '', redirectTo: 'login', pathMatch: 'full' },
    ],
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'home',
        component: HomeComponent,
        children: [
          {
            path: 'my-quotes',
            component: MyQuotesComponent,
          },
          {
            path: 'my-orders',
            component: MyOrdersComponent,
          },
          {
            path: '',
            canActivate: [HomeRedirectGuard],
            children: [],
          },
        ],
      },
      {
        path: 'quotes',
        component: QuotesComponent,
        children: [
          {
            path: 'active-quotes',
            component: ActiveQuotesComponent,
          },
          {
            path: 'archived-quotes',
            component: ArchivedQuotesComponent,
          },
          {
            path: '',
            redirectTo: 'active-quotes',
            pathMatch: 'full',
          },
        ],
      },
      {
        path: 'quotes/add',
        component: ManageQuoteComponent,
        canDeactivate: [PendingChangesGuard],
      },
      {
        path: 'quotes/:id/edit',
        component: ManageQuoteComponent,
        canDeactivate: [PendingChangesGuard],
      },
      {
        path: 'quotes/:id/view',
        component: ViewQuotesComponent,
      },

      {
        path: 'orders',
        children: [
          {
            path: '',
            component: OrdersComponent,
            children: [
              {
                path: 'active-orders',
                component: ActiveOrdersComponent,
                data: { archived: 'false' },
              },
              {
                path: 'archived-orders',
                component: ActiveOrdersComponent,
                data: { archived: 'true' },
              },
              {
                path: 'all-orders',
                component: ActiveOrdersComponent,
                data: { archived: 'all' },
              },
              {
                path: 'saved-lists',
                component: SavedListsComponent,
              },
              {
                path: '',
                redirectTo: 'active-orders',
                pathMatch: 'full',
              },
            ],
          },
          {
            path: ':id/view',
            component: ViewOrdersComponent,
          },
          {
            path: ':id/edit',
            component: ManageOrdersComponent,
            canDeactivate: [PendingChangesGuard],
          },
          {
            path: ':id/view-list',
            component: OrderListViewComponent,
          },
        ],
      },
      {
        path: 'clients',
        component: ClientsComponent,
      },
      {
        path: 'clients/add',
        component: AddClientComponent,
      },
      {
        path: 'clients/edit/:id',
        component: EditClientComponent,
      },
      {
        path: 'clients/edit/:id/duplicate',
        component: EditClientComponent,
      },
      {
        path: 'products',
        loadComponent: () =>
          import('@components/products/products.component').then((c) => c.ProductsComponent),
        children: [
          {
            path: 'item-processes',
            loadComponent: () =>
              import('@components/products/item-processes/item-processes.component').then(
                (m) => m.ItemProcessesComponent,
              ),
          },
          {
            path: 'ca-numbers',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('@components/products/ca-numbers/ca-numbers.component').then(
                    (m) => m.CaNumbersComponent,
                  ),
              },
              {
                path: 'add',
                loadComponent: () =>
                  import(
                    '@components/products/ca-numbers/manage-ca-numbers/manage-ca-numbers.component'
                  ).then((m) => m.ManageCaNumbersComponent),
              },
              {
                path: ':id/edit',
                loadComponent: () =>
                  import(
                    '@components/products/ca-numbers/manage-ca-numbers/manage-ca-numbers.component'
                  ).then((m) => m.ManageCaNumbersComponent),
              },
              {
                path: ':id/view',
                loadComponent: () =>
                  import(
                    '@components/products/ca-numbers/view-ca-number/view-ca-number.component'
                  ).then((m) => m.ViewCaNumberComponent),
              },
            ],
          },
          {
            path: 'products/item-processes',
            loadComponent: () =>
              import('@components/products/item-processes/item-processes.component').then(
                (m) => m.ItemProcessesComponent,
              ),
          },
          {
            path: '',
            redirectTo: 'item-processes',
            pathMatch: 'full',
          },
        ],
      },
      {
        path: 'users',
        component: UsersComponent,
        children: [
          {
            path: 'active',
            component: ActiveUsersComponent,
          },
          {
            path: 'archived',
            component: ArchivedUsersComponent,
          },
          {
            path: '',
            redirectTo: 'active',
            pathMatch: 'full',
          },
        ],
      },
      {
        path: 'users/add',
        component: AddUserComponent,
      },
      {
        path: 'users/edit/:id',
        component: EditUserComponent,
      },
      {
        path: 'users/:id/view',
        component: UserDetailsComponent,
      },
      { path: '', redirectTo: 'home', pathMatch: 'full' },
    ],
  },
  {
    path: '**',
    redirectTo: 'home',
  },
];
